import { useEffect } from 'react';

import {
  classifyCookieSignal,
  fetchTollhouseConsent,
  hasConsentCheckpoint,
  markConsentCheckpoint,
  MAX_POLL_ATTEMPTS,
  POLL_INTERVAL_MS,
  reconcile,
  setConsentViaApi,
  writeTollhouseConsent
} from 'hooks/useTrustArcConsent';

interface UseConsentReconciliationOptions {
  accountConfig?: { url: string };
  isAuthenticated?: boolean;
}

/**
 * App-level consent reconciliation. Fires the Tollhouse GET once per tab session
 * (gated by sessionStorage checkpoint) and aligns cookie consent with the durable record.
 *
 * Key behaviors:
 * - Skips when checkpoint exists (already reconciled this tab session)
 * - Records checkpoint only after full success (GET + action + any write)
 * - Checkpoint invalidates on logout (x-main cookie fingerprint mismatch)
 */
const useConsentReconciliation = (options: UseConsentReconciliationOptions = {}): void => {
  const { accountConfig, isAuthenticated = false } = options;
  const accountUrl = accountConfig?.url;

  useEffect(() => {
    if (!isAuthenticated || !accountUrl) {
      return;
    }

    // Skip if already reconciled this tab session
    if (hasConsentCheckpoint()) {
      return;
    }

    const config = { url: accountUrl };
    let cancelled = false;
    let cmaPollInterval: ReturnType<typeof setInterval> | null = null;

    const writeCookieRejectWhenCmaReady = () => {
      if (window.truste?.cma) {
        setConsentViaApi('REJECT_ALL');
        return;
      }

      let attempts = 0;
      cmaPollInterval = setInterval(() => {
        if (cancelled) {
          if (cmaPollInterval) {
            clearInterval(cmaPollInterval);
          }
          return;
        }

        attempts += 1;

        if (window.truste?.cma) {
          setConsentViaApi('REJECT_ALL');
          if (cmaPollInterval) {
            clearInterval(cmaPollInterval);
          }
          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS && cmaPollInterval) {
          clearInterval(cmaPollInterval);
        }
      }, POLL_INTERVAL_MS);
    };

    const runReconciliation = async () => {
      const originalSignal = classifyCookieSignal();

      // reconcile() is NONE for both; skip the Tollhouse fetch.
      if (originalSignal === 'EU' || originalSignal === 'NO_SIGNAL') {
        return;
      }

      const tollhouseState = await fetchTollhouseConsent(config);

      // Tollhouse unavailable or component unmounted: don't checkpoint
      if (cancelled || tollhouseState === null) {
        return;
      }

      const { action } = reconcile(originalSignal, tollhouseState);

      if (action === 'WRITE_OPT_OUT') {
        const success = await writeTollhouseConsent(config, 'REJECT_ALL');
        if (!cancelled && success) {
          markConsentCheckpoint();
        }
      } else if (action === 'WRITE_OPT_IN') {
        const success = await writeTollhouseConsent(config, 'ACCEPT_ALL');
        if (!cancelled && success) {
          markConsentCheckpoint();
        }
      } else if (action === 'SYNC_COOKIE_REJECT') {
        // Don't checkpoint SYNC_COOKIE_REJECT — we didn't write to Tollhouse,
        // so next page load should still check if the reject was persisted.
        writeCookieRejectWhenCmaReady();
      } else {
        // NONE — reconciliation complete with no action needed
        if (!cancelled) {
          markConsentCheckpoint();
        }
      }
    };

    runReconciliation();

    return () => {
      cancelled = true;
      if (cmaPollInterval) {
        clearInterval(cmaPollInterval);
      }
    };
  }, [accountUrl, isAuthenticated]);
};

export default useConsentReconciliation;
